/**
 * admin/quizzes/$id.tsx — Admin edit library quiz (Phase F.2).
 *
 * Loads GET /api/quiz/admin/:id (handleAdminGetQuiz from Phase E.2),
 * pre-fills the form, PATCH /api/quiz/admin/:id on save. Edits are
 * blocked when the quiz already has activations (would orphan
 * existing attempts). The handler returns 409 INVALID_STATE in that
 * case — we surface it as a banner and lock the form.
 */

import { type ReactNode, useEffect, useState } from 'react';
import { createFileRoute, redirect, useNavigate, useParams } from '@tanstack/react-router';
import { PageHeader } from '../../../components/ui/PageHeader.js';
import { StatePanel } from '../../../components/ui/StatePanel.js';
import {
  QuestionEditor,
  type QuestionDraft,
} from '../../../components/admin/QuestionEditor.js';
import { freshSessionQuery } from '../../../lib/api/queries.js';

export const Route = createFileRoute('/admin/quizzes/$id')({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({ to: '/login', search: { error: undefined, redirect: location.href } });
    }
    if (user.role !== 'admin') {
      throw redirect({ to: '/' });
    }
    return null;
  },
  component: AdminQuizEditPage,
});

interface ApiQuiz {
  id: string;
  title: string;
  description: string | null;
  source: 'library' | 'custom' | 'library_pending';
  sd_grade: number;
  questions: Array<{
    id: string;
    type: 'single_choice' | 'true_false' | 'multi_select';
    difficulty: 'easy' | 'medium' | 'hard';
    prompt: string;
    options_json: string | null;
    correct_answer_json: string;
    points: number;
  }>;
}

function draftFromApi(q: ApiQuiz['questions'][number]): QuestionDraft {
  return {
    type: q.type,
    difficulty: q.difficulty,
    prompt: q.prompt,
    options: q.options_json ? (JSON.parse(q.options_json) as string[]) : null,
    correctAnswer: q.correct_answer_json,
  };
}

function validate(input: { title: string; sdGrade: number; questions: QuestionDraft[] }): string | null {
  if (!input.title.trim()) return 'Judul quiz wajib diisi.';
  if (input.sdGrade < 1 || input.sdGrade > 6) return 'Tingkat harus 1-6.';
  if (input.questions.length === 0) return 'Quiz harus punya minimal 1 soal.';
  for (let i = 0; i < input.questions.length; i++) {
    const q = input.questions[i]!;
    if (!q.prompt.trim()) return `Soal ${i + 1}: pertanyaan wajib diisi.`;
    if (q.type === 'single_choice' || q.type === 'multi_select') {
      const opts = q.options ?? [];
      if (opts.length < 2) return `Soal ${i + 1}: minimal 2 pilihan.`;
      if (opts.some((o) => !o.trim())) return `Soal ${i + 1}: semua pilihan harus diisi.`;
    }
    if (q.type === 'single_choice') {
      const idx = Number(q.correctAnswer);
      if (!Number.isInteger(idx) || idx < 0 || idx >= (q.options?.length ?? 0)) {
        return `Soal ${i + 1}: jawaban benar (pilihan ganda) belum valid.`;
      }
    }
    if (q.type === 'multi_select') {
      const idxs = q.correctAnswer.split(',').filter(Boolean).map(Number);
      if (idxs.length < 2) return `Soal ${i + 1}: minimal 2 jawaban benar.`;
      const optsLen = q.options?.length ?? 0;
      if (idxs.some((n) => !Number.isInteger(n) || n < 0 || n >= optsLen)) {
        return `Soal ${i + 1}: jawaban benar (multi-select) belum valid.`;
      }
    }
  }
  return null;
}

function AdminQuizEditPage(): ReactNode {
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [sdGrade, setSdGrade] = useState(3);
  const [questions, setQuestions] = useState<QuestionDraft[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`/api/quiz/admin/${encodeURIComponent(id)}`, { credentials: 'include' });
        const j = (await r.json()) as { ok: boolean; data?: ApiQuiz; error?: { message: string } };
        if (cancelled) return;
        if (!j.ok || !j.data) {
          setLoadError(j.error?.message ?? 'Gagal memuat quiz.');
          setLoading(false);
          return;
        }
        setTitle(j.data.title);
        setDescription(j.data.description ?? '');
        setSdGrade(j.data.sd_grade);
        setQuestions(j.data.questions.map(draftFromApi));
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setLoadError((err as Error).message);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const updateQuestion = (i: number, q: QuestionDraft): void => {
    setQuestions((prev) => prev.map((old, idx) => (idx === i ? q : old)));
  };
  const removeQuestion = (i: number): void => {
    setQuestions((prev) => prev.filter((_, idx) => idx !== i));
  };
  const addQuestion = (): void => {
    setQuestions((prev) => [
      ...prev,
      { type: 'single_choice', difficulty: 'easy', prompt: '', options: ['Pilihan A', 'Pilihan B'], correctAnswer: '0' },
    ]);
  };

  const onSubmit = async (): Promise<void> => {
    setError(null);
    const validationError = validate({ title, sdGrade, questions });
    if (validationError) {
      setError(validationError);
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`/api/quiz/admin/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim() || undefined,
          sdGrade,
          questions: questions.map((q) => ({
            type: q.type,
            difficulty: q.difficulty,
            prompt: q.prompt.trim(),
            options: q.options,
            correctAnswer: q.correctAnswer,
          })),
        }),
      });
      const j = (await r.json()) as { ok: boolean; error?: { message: string } };
      if (!j.ok) {
        setError(j.error?.message ?? 'Gagal menyimpan quiz.');
        setSubmitting(false);
        return;
      }
      await navigate({ to: '/admin/quizzes' });
    } catch (err) {
      setError((err as Error).message);
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="page-shell">
        <PageHeader eyebrow="Kelola Quiz" title="Memuat quiz…" />
      </main>
    );
  }
  if (loadError) {
    return (
      <main className="page-shell">
        <PageHeader eyebrow="Kelola Quiz" title="Quiz tidak ditemukan" />
        <StatePanel variant="error" title="Gagal memuat quiz" description={loadError} />
      </main>
    );
  }

  return (
    <main className="page-shell">
      <PageHeader
        eyebrow="Kelola Quiz"
        title={`Edit: ${title}`}
        description="Perubahan soal langsung berlaku untuk aktivasi berikutnya. Aktivasi yang sedang berjalan tidak akan terpengaruh."
        action={
          <button
            type="button"
            className="button button-primary"
            onClick={onSubmit}
            disabled={submitting}
            data-testid="quiz-submit"
          >
            {submitting ? 'Menyimpan…' : 'Simpan'}
          </button>
        }
      />

      {error ? (
        <div className="form-message is-error" role="alert" data-testid="quiz-error">
          {error}
        </div>
      ) : null}

      <div className="form-grid">
        <label className="form-field">
          <span className="form-label">Judul</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid="quiz-title"
          />
        </label>
        <label className="form-field">
          <span className="form-label">Tingkat</span>
          <select value={sdGrade} onChange={(e) => setSdGrade(Number(e.target.value))} data-testid="quiz-grade">
            {[1, 2, 3, 4, 5, 6].map((g) => (
              <option key={g} value={g}>Kelas {g}</option>
            ))}
          </select>
        </label>
      </div>

      <label className="form-field">
        <span className="form-label">Deskripsi (opsional)</span>
        <textarea
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </label>

      <section className="section">
        <h2 className="section-title">Soal ({questions.length})</h2>
        <div className="stack">
          {questions.map((q, i) => (
            <QuestionEditor
              key={i}
              index={i}
              value={q}
              onChange={(next) => updateQuestion(i, next)}
              onRemove={() => removeQuestion(i)}
            />
          ))}
        </div>
        <div className="cluster" style={{ marginTop: 'var(--spacing-3)' }}>
          <button
            type="button"
            className="button button-secondary"
            onClick={addQuestion}
            data-testid="quiz-add-question"
          >
            + Tambah Soal
          </button>
        </div>
      </section>
    </main>
  );
}
